import React from 'react';
import { Lightbulb, TrendingUp, Users, Target, Brain } from 'lucide-react';

interface InsightData {
  insight_number: number;
  headline: string;
  supporting_evidence: string;
  quote_text?: string;
  quote_attribution?: string;
  analysis: string;
  insight_type?: string;
  confidence_level?: string;
}

interface AIInsightsJSON {
  key_findings?: {
    primary_answer_breakdown: string;
    categorical_distribution: string;
  };
  insights: InsightData[];
  total_respondents?: number;
  data_quality_notes?: string[];
  segmentation_summary?: any;
}

interface AIInsightCardProps {
  insights: string[] | AIInsightsJSON;
}

const AIInsightCard: React.FC<AIInsightCardProps> = ({ insights }) => {
  // Check if insights is the new JSON format or old string array
  const isJSONFormat = insights && typeof insights === 'object' && 'insights' in insights;

  // Handle new JSON format
  if (isJSONFormat) {
    const jsonInsights = insights as AIInsightsJSON;
    const parsedInsights = jsonInsights.insights.map((insight) => ({
      type: 'structured' as const,
      number: insight.insight_number,
      title: insight.headline,
      evidence: insight.supporting_evidence,
      quote: insight.quote_text && insight.quote_attribution ? {
        author: insight.quote_attribution,
        text: insight.quote_text
      } : null,
      analysis: insight.analysis,
      insightType: insight.insight_type,
      confidenceLevel: insight.confidence_level
    }));

    return <RenderInsights insights={parsedInsights} />;
  }

  // Handle old string array format (backward compatibility)
  const stringInsights = insights as string[];
  const parsedInsights = stringInsights.map((insight, index) => {
    // Clean up the insight text - remove trailing commas and quotes
    let cleanedInsight = insight.trim();
    
    // Remove trailing commas and quotes
    cleanedInsight = cleanedInsight.replace(/[,'"]+\s*$/, '');
    
    // Remove leading quotes if present
    cleanedInsight = cleanedInsight.replace(/^["']/, '');
    
    // Check if this is a formatted insight with "Insight #X:" pattern
    const hasInsightFormat = cleanedInsight.match(/^Insight #\d+:/);
    
    if (hasInsightFormat) {
      // This is a structured insight - parse all components
      const fullText = cleanedInsight;
      
      // Extract title - more robust regex
      const titleMatch = fullText.match(/^Insight #\d+:\s*([^.]+?)(?=\s+Supporting evidence:)/s);
      const title = titleMatch ? titleMatch[1].trim() : '';
      
      // Extract supporting evidence - look for content between "Supporting evidence:" and either a quote or "Analysis:"
      const evidenceMatch = fullText.match(/Supporting evidence:\s*([^'"]+?)(?=\s*[A-Za-z\s]+(?:said|noted|stated|explained):|Analysis:)/s);
      const evidence = evidenceMatch ? evidenceMatch[1].trim() : '';
      
      // Extract quoted text if present - improved regex to handle names better
      const quoteMatch = fullText.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+(?:said|noted|stated|explained):\s*['"']([^'"']+)['"']/);
      const quote = quoteMatch ? {
        author: quoteMatch[1].trim(),
        text: quoteMatch[2].trim()
      } : null;
      
      // Extract analysis - everything after "Analysis:" to the end
      const analysisMatch = fullText.match(/Analysis:\s*(.+)$/s);
      const analysis = analysisMatch ? analysisMatch[1].trim() : '';
      
      // Extract insight number
      const numberMatch = fullText.match(/^Insight #(\d+):/);
      const insightNumber = numberMatch ? parseInt(numberMatch[1]) : index + 1;
      
      return {
        type: 'structured' as const,
        number: insightNumber,
        title,
        evidence,
        quote,
        analysis,
        original: cleanedInsight
      };
    } else {
      // This is a regular insight
      return {
        type: 'simple' as const,
        text: cleanedInsight,
        original: cleanedInsight
      };
    }
  });

  return <RenderInsights insights={parsedInsights} />;
};

// Separate component for rendering insights
interface ParsedInsight {
  type: 'structured' | 'simple';
  number?: number;
  title?: string;
  evidence?: string;
  quote?: { author: string; text: string } | null;
  analysis?: string;
  text?: string;
  original?: string;
  insightType?: string;
  confidenceLevel?: string;
}

const RenderInsights: React.FC<{ insights: ParsedInsight[] }> = ({ insights }) => {
  // Icons for different insight numbers
  const getIcon = (number: number) => {
    const icons = [
      <Users className="h-5 w-5" />,
      <Target className="h-5 w-5" />,
      <TrendingUp className="h-5 w-5" />,
      <Brain className="h-5 w-5" />,
      <Lightbulb className="h-5 w-5" />
    ];
    return icons[(number - 1) % icons.length];
  };

  // Minimalistic color scheme - using brand colors
  const getColorScheme = () => {
    return {
      bg: 'bg-white',
      border: 'border-gray-200',
      icon: 'text-[#07E5D1]',
      accent: 'bg-[#E8FCFA]'
    };
  };

  return (
    <div className="space-y-4">
      {insights.map((insight, idx) => {
        if (insight.type === 'structured') {
          const colorScheme = getColorScheme();
          
          return (
            <div 
              key={idx} 
              className={`${colorScheme.bg} ${colorScheme.border} border rounded-lg p-6 space-y-4 transition-all duration-200 hover:shadow-sm`}
            >
              {/* Header */}
              <div className="flex items-start gap-4">
                <div className={`${colorScheme.accent} ${colorScheme.icon} p-2.5 rounded-lg flex-shrink-0`}>
                  {getIcon(insight.number || 1)}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-[#1C1C1E] text-lg leading-snug">
                    {insight.title}
                  </h4>
                  <p className="text-xs text-gray-500 mt-1">Insight #{insight.number}</p>
                </div>
              </div>

              {/* Content */}
              <div className="space-y-4 text-sm">
                {/* Supporting Evidence */}
                <div>
                  <p className="text-[#6B7280] text-xs uppercase tracking-wide font-medium mb-1.5">Supporting Evidence</p>
                  <p className="text-[#374151] leading-relaxed">{insight.evidence}</p>
                </div>

                {/* Quote if present - as a single distinct UI element */}
                {insight.quote && (
                  <div className="relative my-4">
                    {/* Quote icon positioned above the box */}
                    <div className="absolute -top-3 left-8 bg-white px-2">
                      <svg className="w-8 h-8 text-[#07E5D1]" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                      </svg>
                    </div>
                    <div className="bg-[#E8FCFA] rounded-lg px-8 py-6 border border-[#07E5D1]/20">
                      <p className="text-[#374151] italic leading-relaxed text-sm text-left pl-4 pt-2">
                        {insight.quote.text}
                      </p>
                      <p className="text-xs text-[#6B7280] mt-3 text-right pr-2">
                        — {insight.quote.author}
                      </p>
                    </div>
                  </div>
                )}

                {/* Analysis */}
                <div>
                  <p className="text-[#6B7280] text-xs uppercase tracking-wide font-medium mb-1.5">Analysis</p>
                  <p className="text-[#374151] leading-relaxed">{insight.analysis}</p>
                </div>
              </div>
            </div>
          );
        } else {
          // Simple insight
          return (
            <div key={idx} className="bg-white border border-gray-200 rounded-lg p-5">
              <p className="text-sm text-[#374151] leading-relaxed flex items-start gap-3">
                <Lightbulb className="h-4 w-4 text-[#07E5D1] mt-0.5 flex-shrink-0" />
                <span>{insight.text}</span>
              </p>
            </div>
          );
        }
      })}
    </div>
  );
};

export default AIInsightCard;